// src/ConsultorCadastro.js
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { api } from './api';
import { recarregar } from './dados';
import { PageHeader, Card, Campo, Botao, Busca, Aviso, Avatar, Vazio, inputCls, normalizar, IconMaleta, IconMais, IconLapis, IconLixo, IconEmail } from './ui';

const VAZIO = { idFirebase: '', nrConsultorSequencial: '', nome: '', email: '', ativo: true, soAcesso: false, tipo: 'CONSULTOR' };

const ConsultorCadastro = ({ consultores, currentUser, setCurrentView }) => {
    const [formData, setFormData] = useState(VAZIO);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [aviso, setAviso] = useState(null);
    const [busca, setBusca] = useState('');

    // chave "só entra quem está cadastrado aqui"
    const [soCadastrados, setSoCadastrados] = useState(null); // null = carregando
    const [salvandoChave, setSalvandoChave] = useState(false);
    useEffect(() => {
        let vivo = true;
        api.get('/config/acesso').then((r) => { if (vivo) setSoCadastrados(!!r.soCadastrados); }).catch(() => {});
        return () => { vivo = false; };
    }, [consultores]);

    const alternarSoCadastrados = async () => {
        const ligar = !soCadastrados;
        const texto = ligar
            ? 'Restringir o acesso?\n\nSó vão conseguir entrar no sistema os e-mails cadastrados nesta tela e ativos. Quem não estiver cadastrado volta para o login na hora.\n\nConfira antes se todos que usam o sistema (inclusive sócios) estão na lista.'
            : 'Liberar o acesso?\n\nQualquer pessoa com login no Firebase volta a conseguir entrar (os consultores inativos continuam bloqueados).';
        if (!window.confirm(texto)) return;
        setSalvandoChave(true);
        try {
            const r = await api.put('/config/acesso', { soCadastrados: ligar });
            setSoCadastrados(!!r.soCadastrados);
            setAviso({ tipo: 'ok', texto: r.soCadastrados ? 'Acesso restrito: só entram os e-mails cadastrados e ativos.' : 'Acesso liberado para todos os logins do Firebase.' });
        } catch (err) {
            setAviso({ tipo: 'erro', texto: err.message });
        } finally {
            setSalvandoChave(false);
        }
    };
    const fecharAviso = useCallback(() => setAviso(null), []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value.toUpperCase() }));
    };

    const handleNewConsultor = () => {
        setFormData(VAZIO);
        setIsEditing(false);
    };

    const handleEditConsultor = (consultor) => {
        setFormData({
            idFirebase: consultor.idFirebase,
            nrConsultorSequencial: consultor.nrConsultorSequencial || '',
            nome: consultor.nome || '',
            email: consultor.email || '',
            ativo: consultor.ativo !== false,
            soAcesso: consultor.soAcesso === true,
            tipo: consultor.tipo === 'ADMIN' ? 'ADMIN' : 'CONSULTOR',
        });
        setIsEditing(true);
        setAviso(null);
    };

    const handleSaveConsultor = async (e) => {
        e.preventDefault();
        const nome = formData.nome.trim();
        const email = formData.email.trim();
        if (!nome || !email) {
            setAviso({ tipo: 'erro', texto: 'Nome e e-mail são obrigatórios.' });
            return;
        }
        const original = consultores.find(c => c.idFirebase === formData.idFirebase);
        const inativando = isEditing && !formData.ativo && original && original.ativo !== false;
        if (inativando && !window.confirm(`Inativar ${nome.toUpperCase()}?\n\nO acesso dele ao sistema será bloqueado na hora: as telas abertas voltam para o login e ele não consegue mais entrar.`)) return;
        setLoading(true);
        try {
            // o servidor confere nome e e-mail repetidos e devolve o motivo
            if (isEditing) {
                await api.put(`/consultores/${formData.idFirebase}`, { nome, email, ativo: formData.ativo, soAcesso: formData.soAcesso, tipo: formData.tipo });
                await recarregar('consultores');
                recarregar('pendencias');
                const situacao = inativando ? ' e inativado (acesso bloqueado)' : (original && original.ativo === false && formData.ativo ? ' e reativado (acesso liberado)' : '');
                setAviso({ tipo: 'ok', texto: `Consultor ${nome.toUpperCase()} alterado${situacao}.` });
            } else {
                await api.post('/consultores', { nome, email, ativo: formData.ativo, soAcesso: formData.soAcesso, tipo: formData.tipo });
                await recarregar('consultores');
                setAviso({ tipo: 'ok', texto: `Consultor ${nome.toUpperCase()} cadastrado.` });
            }
            handleNewConsultor();
        } catch (err) {
            console.error("Erro ao salvar consultor:", err);
            setAviso({ tipo: 'erro', texto: err.message });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteConsultor = async (consultor) => {
        if (!window.confirm(`Excluir o consultor ${consultor.nome}? Esta ação não pode ser desfeita.`)) return;
        setLoading(true);
        try {
            await api.del(`/consultores/${consultor.idFirebase}`);
            await recarregar('consultores');
            setAviso({ tipo: 'ok', texto: `Consultor ${consultor.nome} excluído.` });
            if (formData.idFirebase === consultor.idFirebase) handleNewConsultor();
        } catch (err) {
            console.error("Erro ao excluir consultor:", err);
            setAviso({ tipo: 'erro', texto: err.message });
        } finally {
            setLoading(false);
        }
    };

    const lista = useMemo(() => {
        const t = normalizar(busca);
        return consultores.filter(c => !t || normalizar(c.nome).includes(t) || normalizar(c.email).includes(t));
    }, [consultores, busca]);
    const inativos = consultores.filter(c => c.ativo === false).length;
    const ehEuMesmo = normalizar(formData.email) !== '' && normalizar(formData.email) === normalizar(currentUser && currentUser.email);

    return (
        <div className="space-y-4">
            <PageHeader icon={IconMaleta} cor="blue" titulo="Consultores" subtitulo={`${consultores.length} consultor${consultores.length === 1 ? '' : 'es'} cadastrado${consultores.length === 1 ? '' : 's'}${inativos ? ` (${inativos} inativo${inativos === 1 ? '' : 's'})` : ''}`}>
                <Botao variante="primario" onClick={handleNewConsultor}><IconMais /> Novo consultor</Botao>
            </PageHeader>

            <Aviso aviso={aviso} onFechar={fecharAviso} />

            {/* ---------- controle de acesso ---------- */}
            {soCadastrados !== null && (
                <div className={`flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border px-4 py-3 ${soCadastrados ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}>
                    <div className="flex-1 text-sm">
                        <div className={`font-semibold ${soCadastrados ? 'text-green-800' : 'text-amber-800'}`}>
                            {soCadastrados ? 'Acesso restrito aos cadastrados' : 'Acesso aberto a qualquer login do Firebase'}
                        </div>
                        <div className="text-gray-600 text-xs mt-0.5">
                            {soCadastrados
                                ? 'Só entram no sistema os e-mails desta lista que estão ativos.'
                                : 'Quem não está nesta lista também consegue entrar. Cadastre todos que usam o sistema (marque "Somente acesso" para quem não atende pendências) e depois restrinja.'}
                        </div>
                    </div>
                    <Botao variante={soCadastrados ? 'secundario' : 'primario'} disabled={salvandoChave} onClick={alternarSoCadastrados} className="whitespace-nowrap">
                        {salvandoChave ? 'Salvando...' : soCadastrados ? 'Liberar para todos' : 'Restringir aos cadastrados'}
                    </Botao>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-[22rem_1fr] gap-4 items-start">
                {/* ---------- formulário ---------- */}
                <Card
                    className="lg:sticky lg:top-0"
                    titulo={isEditing ? `Editando consultor${formData.nrConsultorSequencial ? ` · Nr ${formData.nrConsultorSequencial}` : ''}` : 'Novo consultor'}
                >
                    <form onSubmit={handleSaveConsultor} className="space-y-3">
                        <Campo rotulo="Nome" obrigatorio>
                            <input type="text" name="nome" value={formData.nome} onChange={handleInputChange} className={`${inputCls} uppercase`} autoComplete="off" />
                        </Campo>
                        <Campo rotulo="E-mail" obrigatorio>
                            <input type="email" name="email" value={formData.email} onChange={handleInputChange} className={inputCls} autoComplete="off" />
                        </Campo>
                        <Campo rotulo="Tipo" ajuda={formData.tipo === 'ADMIN'
                            ? 'Admin: acessa tudo, inclusive os cadastros (consultores, empresas, módulos).'
                            : 'Consultor: pendências, calendário, concluídas e dashboard. Não vê os cadastros.'}>
                            <select value={formData.tipo} disabled={ehEuMesmo && formData.tipo === 'ADMIN'}
                                onChange={(e) => setFormData(p => ({ ...p, tipo: e.target.value }))}
                                className={inputCls} title={ehEuMesmo ? 'Você não pode tirar o seu próprio perfil de administrador' : ''}>
                                <option value="CONSULTOR">Consultor</option>
                                <option value="ADMIN">Admin</option>
                            </select>
                        </Campo>
                        <label className={`flex items-start gap-2 text-sm text-gray-700 select-none ${ehEuMesmo ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                            <input type="checkbox" checked={formData.ativo} disabled={ehEuMesmo && formData.ativo}
                                onChange={(e) => setFormData(p => ({ ...p, ativo: e.target.checked }))}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                            <span>
                                Ativo
                                <span className="block text-xs text-gray-500">
                                    {ehEuMesmo
                                        ? 'Você não pode inativar o seu próprio usuário.'
                                        : 'Desmarcado, o acesso ao sistema é bloqueado na hora e ele deixa de aparecer como técnico nas pendências novas.'}
                                </span>
                            </span>
                        </label>
                        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer select-none">
                            <input type="checkbox" checked={formData.soAcesso}
                                onChange={(e) => setFormData(p => ({ ...p, soAcesso: e.target.checked }))}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                            <span>
                                Somente acesso
                                <span className="block text-xs text-gray-500">Entra no sistema, mas não aparece como técnico nas pendências nem nos filtros (ex.: sócio, gerente).</span>
                            </span>
                        </label>
                        <div className="flex flex-wrap gap-2 pt-1">
                            <Botao type="submit" variante="primario" disabled={loading} className="flex-1">
                                {loading ? 'Salvando...' : isEditing ? 'Salvar alterações' : 'Cadastrar'}
                            </Botao>
                            {isEditing && <Botao onClick={handleNewConsultor} disabled={loading}>Cancelar</Botao>}
                        </div>
                        {isEditing && (
                            <button type="button" onClick={() => handleDeleteConsultor(formData)} disabled={loading}
                                className="w-full text-xs text-red-600 hover:text-red-800 pt-1">
                                Excluir este consultor
                            </button>
                        )}
                    </form>
                </Card>

                {/* ---------- lista ---------- */}
                <Card semPadding titulo={`Cadastrados (${lista.length})`} acao={<Busca valor={busca} onChange={setBusca} placeholder="Buscar nome ou e-mail" className="w-full sm:w-64" />}>
                    {lista.length === 0 ? (
                        <Vazio>{consultores.length ? 'Nenhum consultor encontrado.' : 'Nenhum consultor cadastrado.'}</Vazio>
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {lista.map((c) => {
                                const ativo = c.idFirebase === formData.idFirebase;
                                return (
                                    <li key={c.idFirebase}
                                        onClick={() => handleEditConsultor(c)}
                                        className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer ${ativo ? 'bg-indigo-50' : 'hover:bg-gray-50'} ${c.ativo === false ? 'opacity-60' : ''}`}>
                                        <Avatar nome={c.nome} />
                                        <div className="min-w-0 flex-1">
                                            <div className="font-semibold text-gray-800 text-sm truncate flex items-center gap-2">
                                                <span className="truncate">{c.nome}</span>
                                                {c.ativo === false && <span className="flex-shrink-0 text-[11px] font-semibold uppercase tracking-wide text-red-700 bg-red-100 rounded px-1.5 py-0.5">Inativo · sem acesso</span>}
                                                {c.tipo === 'ADMIN' && <span className="flex-shrink-0 text-[11px] font-semibold uppercase tracking-wide text-purple-700 bg-purple-100 rounded px-1.5 py-0.5">Admin</span>}
                                                {c.ativo !== false && c.soAcesso && <span className="flex-shrink-0 text-[11px] font-semibold uppercase tracking-wide text-blue-700 bg-blue-100 rounded px-1.5 py-0.5">Somente acesso</span>}
                                            </div>
                                            <div className="text-xs text-gray-500 truncate flex items-center gap-1"><IconEmail className="h-3.5 w-3.5" />{c.email || '—'}</div>
                                        </div>
                                        {c.nrConsultorSequencial && <span className="text-xs text-gray-400 hidden sm:inline">Nr {c.nrConsultorSequencial}</span>}
                                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                            <Botao tamanho="sm" variante="fantasma" title="Editar" onClick={() => handleEditConsultor(c)}><IconLapis /></Botao>
                                            <Botao tamanho="sm" variante="fantasma" title="Excluir" className="text-red-500 hover:bg-red-50" onClick={() => handleDeleteConsultor(c)}><IconLixo /></Botao>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default ConsultorCadastro;
